import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  UsersIconFilled,
  MinusIcon,
  PhoneIcon,
  PlusIcon,
} from '@rsos/assets/icons';
import ChatAvatar from '@rsos/components/capstone/Chat/ChatAvatar';
import { CENTRAL_STATION } from '@rsos/constants/appNames.js';
import { AlertAuthorizedEntity } from '@rsos/reduxDataStore/src/features/alerts/alerts.types';
import { type IncidentsState } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import type { ChatParticipant } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { type EmergencyCallsState } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { type SinatraState } from '@rsos/sinatra';
import Popover from '../../base/Popover';
import ChatParticipants from '../ChatParticipants';
import {
  Container,
  AvatarBox,
  AgencyInfo,
  AgencyName,
  AgencyPhone,
  PhoneNumber,
  AgenciesTitle,
  AgenciesList,
  ActionsBox,
  ActionButton,
  UnreadPill,
  LineClamp,
  chatHeaderIconColor,
} from './ChatHeader.styles';

export interface ChatHeaderProps {
  alertChatParticipant?: AlertAuthorizedEntity;
  callChatParticipants?: ChatParticipant[];
  isChatBoxVisible: boolean;
  isUnread: boolean;
  onActionButtonClick: () => void;
}

interface RootState {
  sinatra: SinatraState;
  incidents: IncidentsState;
  emergencyCalls: EmergencyCallsState;
}

const ChatHeader = ({
  alertChatParticipant,
  callChatParticipants,
  isChatBoxVisible,
  isUnread,
  onActionButtonClick,
}: ChatHeaderProps) => {
  const isCentralStation = useSelector(
    (state: RootState) =>
      state.sinatra.user.currentRole?.application === CENTRAL_STATION,
  );
  const [isParticipantsPopoverOpen, setIsParticipantsPopoverOpen] =
    useState(false);

  const selectedAlertID = useSelector(
    (state: RootState) => state.incidents.selectedAlertID,
  );
  const selectedCallerID = useSelector(
    (state: RootState) => state?.emergencyCalls?.selectedCallerID,
  );
  const fetchAlertChatParticipantName = (
    alertChatParticipant: AlertAuthorizedEntity | undefined,
  ) => {
    return alertChatParticipant && alertChatParticipant.name
      ? alertChatParticipant.name
      : 'ECC Unavailable';
  };
  return (
    <Container isUnread={isUnread} isChatBoxVisible={isChatBoxVisible}>
      {(alertChatParticipant || isCentralStation) && (
        <>
          <AvatarBox>
            <ChatAvatar
              name={fetchAlertChatParticipantName(alertChatParticipant)}
            />
          </AvatarBox>
          <AgencyInfo>
            <AgencyName>
              {fetchAlertChatParticipantName(alertChatParticipant)}
            </AgencyName>
            {alertChatParticipant && alertChatParticipant.phone && (
              <AgencyPhone>
                <PhoneIcon
                  width={10}
                  height={12}
                  viewboxWidth={11}
                  viewboxHeight={15}
                  color={chatHeaderIconColor}
                />
                <PhoneNumber>{alertChatParticipant.phone}</PhoneNumber>
              </AgencyPhone>
            )}
          </AgencyInfo>
        </>
      )}
      {callChatParticipants && callChatParticipants.length > 0 && (
        <>
          <AvatarBox>
            <ChatAvatar
              name={callChatParticipants[0].display_name}
              isOverlapped={callChatParticipants.length > 1}
            />
          </AvatarBox>
          <AgencyInfo>
            <LineClamp>
              <AgenciesTitle>
                {callChatParticipants.length > 1
                  ? `${callChatParticipants.length} Agencies`
                  : '1 Agency'}
              </AgenciesTitle>{' '}
              {callChatParticipants.map((participant, index) => [
                index > 0 && ', ',
                <AgenciesList key={index}>
                  {participant.display_name}
                </AgenciesList>,
              ])}
            </LineClamp>
          </AgencyInfo>
        </>
      )}

      <ActionsBox>
        {isUnread && !isChatBoxVisible ? (
          <UnreadPill>New</UnreadPill>
        ) : (
          <ActionButton
            onMouseEnter={() => setIsParticipantsPopoverOpen(true)}
            onMouseLeave={() => setIsParticipantsPopoverOpen(false)}
          >
            <Popover
              cssDisplayProperty="block"
              isOpen={isParticipantsPopoverOpen}
              popoverID={`chat-popover`}
              trigger={
                <UsersIconFilled
                  width={12}
                  height={12}
                  color={chatHeaderIconColor}
                />
              }
              useCustomOffset
              xOffset={-6}
              yOffset={20}
            >
              <ChatParticipants
                alertChatParticipants={
                  selectedAlertID && alertChatParticipant
                    ? [alertChatParticipant]
                    : null
                }
                callChatParticipants={
                  selectedCallerID ? callChatParticipants : []
                }
              />
            </Popover>
          </ActionButton>
        )}
        <ActionButton noMargin showBorder onClick={onActionButtonClick}>
          {!isChatBoxVisible ? (
            <PlusIcon width={14} height={14} color={chatHeaderIconColor} />
          ) : (
            <MinusIcon width={12} height={12} color={chatHeaderIconColor} />
          )}
        </ActionButton>
      </ActionsBox>
    </Container>
  );
};

export default ChatHeader;
